import type { BusinessObjectRef } from "~/api/generated/record-service/model";

/**
 * The two shapes record-service answers with, in one place.
 *
 * Both were re-implemented in six and five files respectively — every copy
 * carrying the same defensive checks because the BE's own contract is looser
 * than its OpenAPI says. Consolidating them is not tidiness: when one copy was
 * hardened against a new payload variant, the others silently kept the old bug.
 */

/**
 * Child references from a relationship read.
 *
 * The endpoint's schema declares `BusinessObjectRef[]`, but it answers with
 * `{ items: [...] }` for some resources — so both are accepted, and anything
 * without a string `primaryKey` is dropped rather than passed on as a ref that
 * later reads will 404 on.
 */
export const parseChildRefs = (raw: unknown): BusinessObjectRef[] => {
  const list = Array.isArray(raw) ? raw : (raw as { items?: unknown } | null)?.items;

  if (!Array.isArray(list)) {
    return [];
  }

  return list.filter(
    (item): item is BusinessObjectRef =>
      item != null &&
      typeof item === "object" &&
      typeof (item as BusinessObjectRef).primaryKey === "string",
  );
};

/**
 * The record inside a `dereference` response.
 *
 * `customFetch`'s envelope unwrap only fires for a pure `{ data }` body; the
 * dereference endpoint answers `{ data, _links }`, so the record arrives still
 * wrapped. The `_links` probe is what distinguishes that from a record that
 * genuinely has its own `data` property.
 */
export const unwrapDereferenced = (body: unknown): Record<string, unknown> => {
  if (body == null || typeof body !== "object") {
    return {};
  }

  const outer = body as Record<string, unknown>;
  const inner = outer.data;

  if ("_links" in outer && inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }

  return outer;
};
